import * as React from 'react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { TerminAttachment, deleteTerminAttachment } from 'api/terminAttachment';
import { getApiUrl } from 'api/useApi';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import { AddIcon, DeleteIcon, EditIcon } from '@fluentui/react-icons';
import { formStyles } from 'styles/formStyles';
import { EditTerminAttachment } from './EditTerminAttachment';

const styles = mergeStyleSets({
    header: {
        display: 'grid',
        gridTemplateColumns: '1fr max-content',
    },
    title: {
        fontWeight: 600,
        marginTop: 5,
    },
    list: {
        marginTop: 5,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr max-content max-content',
        gridColumnGap: 10,
    },
    addAction: {
        cursor: 'pointer',
        marginTop: 7,
    },
});

interface TerminAttachmentListProps {
    zfKaNr: number;
    terminAttachmentList: TerminAttachment[];
    refetchTermin: () => void;
    onAdd: () => void;
    disabled?: boolean;
}

export const TerminAttachmentList: React.FC<TerminAttachmentListProps> = ({
    zfKaNr,
    terminAttachmentList,
    refetchTermin,
    onAdd,
    disabled,
}) => {
    const auth = useSelector(authSelector);

    const [editTerminAttachmentData, setEditTerminAttachmentData] = React.useState<TerminAttachment | null>(null);
    const [deleteTerminAttachmentData, setDeleteTerminAttachmentData] = React.useState<TerminAttachment | null>(null);

    return (
        <>
            <div className={styles.header}>
                <div className={styles.title}>Dokumente</div>
                {disabled || <AddIcon className={styles.addAction} onClick={onAdd} />}
            </div>
            <div className={styles.list}>
                {terminAttachmentList.map((terminAttachment) => {
                    const fileUrl =
                        getApiUrl('terminattachment') +
                        '?zfKaNr=' +
                        zfKaNr +
                        '&fileName=' +
                        encodeURIComponent(terminAttachment.file);

                    return (
                        <>
                            <div>{terminAttachment.text}</div>
                            <div>
                                <a href={fileUrl} rel="noreferrer" target="_blank">
                                    {terminAttachment.file}
                                </a>
                            </div>
                            {(auth.rolle === 'GF' || auth.rolle === 'ID') && (
                                <>
                                    <EditIcon
                                        className={formStyles.positionenAction}
                                        onClick={() => setEditTerminAttachmentData(terminAttachment)}
                                    />
                                    <DeleteIcon
                                        className={formStyles.positionenAction}
                                        onClick={() => setDeleteTerminAttachmentData(terminAttachment)}
                                    />
                                </>
                            )}
                        </>
                    );
                })}
            </div>
            {editTerminAttachmentData && (
                <EditTerminAttachment
                    terminAttachment={editTerminAttachmentData}
                    onClose={(refetch) => {
                        if (refetch) {
                            refetchTermin();
                        }
                        setEditTerminAttachmentData(null);
                    }}
                />
            )}
            {deleteTerminAttachmentData && (
                <ConfirmMessage
                    title="Dokument löschen"
                    minWidth={500}
                    content={
                        <>
                            Soll das Dokument{' '}
                            {deleteTerminAttachmentData.file + ' / ' + deleteTerminAttachmentData.text} gelöscht werden?
                        </>
                    }
                    onConfirm={() => {
                        if (deleteTerminAttachmentData) {
                            deleteTerminAttachment(deleteTerminAttachmentData).then(() => {
                                refetchTermin();
                                setDeleteTerminAttachmentData(null);
                            });
                        }
                    }}
                    onCancel={() => setDeleteTerminAttachmentData(null)}
                />
            )}
        </>
    );
};
